import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import { motionValue, useTransform, type MotionValue } from 'framer-motion';

/** Mola da largura da sidebar (abrir/recolher e fixar/desafixar). */
export const SIDEBAR_SPRING = { type: 'spring' as const, stiffness: 360, damping: 36, mass: 0.8 };

interface SidebarMotion {
  /** 0 = trilho recolhido, 1 = aberta. Sai direto da largura animada do <aside>. */
  progress: MotionValue<number>;
  /** Opacidade de rótulos e textos: entram na segunda metade da abertura. */
  reveal: MotionValue<number>;
  /** Opacidade do que só existe no trilho (divisores, % do anel de saldo). */
  rail: MotionValue<number>;
  /** false no drawer mobile, que é sempre aberto e não anima largura. */
  animated: boolean;
}

const STATIC_OPEN: SidebarMotion = {
  progress: motionValue(1),
  reveal: motionValue(1),
  rail: motionValue(0),
  animated: false,
};

const SidebarMotionContext = createContext<SidebarMotion>(STATIC_OPEN);

/**
 * Tudo que morfa na sidebar (rótulos, marca, saldo, pílula ativa) lê o mesmo
 * progresso, derivado da largura em cada frame. Nada termina antes da borda nem
 * troca de layout no fim — é isso que fazia os itens "pularem" ao recolher.
 */
export function SidebarMotionProvider({
  progress,
  children,
}: {
  progress: MotionValue<number>;
  children: ReactNode;
}) {
  const reveal = useTransform(progress, [0.45, 1], [0, 1]);
  const rail = useTransform(progress, [0, 0.4], [1, 0]);
  const value = useMemo(() => ({ progress, reveal, rail, animated: true }), [progress, reveal, rail]);
  return createElement(SidebarMotionContext.Provider, { value }, children);
}

export function useSidebarMotion(): SidebarMotion {
  return useContext(SidebarMotionContext);
}
